import { Injectable } from "@angular/core";
import { environment } from "@env/environment";
import { BehaviorSubject, forkJoin, Observable, of } from "rxjs";
import { catchError, map, retry, tap } from "rxjs/operators";
import { TermsService } from "./terms.service";
import isEqual from "lodash/isEqual";
import cloneDeep from "lodash/cloneDeep";
import { HttpUtilService } from "@services/http-util.service";
import { EnvironmentService } from "@services/environment.service";
import { NotificationsService } from "@services/notifications.service";

export enum LmsType {
  Blackboard = "blackboard",
  Brightspace = "brightspace",
  Canvas = "canvas",
  Jenzabar = "jenzabar",
  Moodle = "moodle",
  Sakai = "sakai",
  Schoology = "schoology",
}

@Injectable({
  providedIn: "root",
})
export class IntegrationService {
  private _integrations = new BehaviorSubject({
    items: [],
    totalItems: 0,
    isLoadingInitial: true,
    isLoading: false,
  });
  public integrations$ = this._integrations.asObservable();
  public integrationsSearchInput = {
    perPage: 10,
    pageNumber: 1,
    searchTerm: "",
  };

  public originalForm;
  startLoadingIntegrations: boolean = true;
  isLoadingIntegrations: boolean;
  constructor(
    private notifications: NotificationsService,
    private environmentService: EnvironmentService,
    private http: HttpUtilService,
    private termsService: TermsService
  ) {}

  getAllIntegrations$(): Observable<any> {
    let url = `${environment.apiRoot}/integrations?read=json_data&created=true`;
    return this.http.request("get", url);
  }

  getIntegrations$(input): Observable<any> {
    if (this.startLoadingIntegrations) {
      this.getIntegrationsNetworkCall$(input).subscribe();
    }
    this.startLoadingIntegrations = false;
    return this.integrations$;
  }

  resetLoadingIntegrationsState() {
    this.startLoadingIntegrations = true;
  }

  handleSuccessfulDeletion(id) {
    const integrations = this._integrations.getValue();
    let index;
    integrations.items.find((item, i) => {
      if (item.id === id) {
        index = i;
        return item;
      }
    });
    if (index === undefined) return;
    integrations.items.splice(index, 1);
    this._integrations.next(integrations);
  }

  deleteIntegration$(id) {
    return this.http
      .request("delete", environment.apiRoot + "/integrations" + `?id=${id}`)
      .pipe(catchError((error) => of(error)))
      .pipe(
        tap((response) => {
          if (response?.status === "ok") this.handleSuccessfulDeletion(id);
        })
      );
  }

  getIntegrationNetworkCall$(id): Observable<any> {
    return this.http
      .request(
        "get",
        `${environment.apiRoot}/integrations?read=json_data&id=${id}`
      )
      .pipe(retry(3))
      .pipe(catchError((error) => of({ error })))
      .pipe(
        map((response) => {
          if (Array.isArray(response) && response.length === 1) {
            return response[0];
          }
        })
      );
  }

  getIntegration$(id): Observable<any> {
    const integration = this._integrations
      .getValue()
      .items.find((item) => item.id === id);
    return integration
      ? of(integration)
      : this.http
          .request(
            "get",
            `${environment.apiRoot}/integrations?read=json_data&id=${id}`
          )
          .pipe(catchError((error) => of({ error })))
          .pipe(
            map((response) => {
              if (Array.isArray(response) && response.length === 1) {
                return response[0];
              }
            })
          );
  }

  getIntegrationsNetworkCall$({
    perPage,
    pageNumber,
    searchTerm,
  }): Observable<any> {
    perPage = perPage || 0;
    pageNumber = pageNumber - 1 || 0;
    if (pageNumber < 0) pageNumber = 0;
    this.integrationsSearchInput = { perPage, pageNumber, searchTerm };
    this._integrations.next({
      ...this._integrations.getValue(),
      isLoading: true,
    });
    let url = `${environment.apiRoot}/integrations?read=json_data&created=true&perPage=${perPage}&pageNumber=${pageNumber}`;
    if (searchTerm) url += `&searchTerm=${searchTerm}`;

    return environment.isOPM
      ? forkJoin([
          this.http.request("get", url, null, null, true),
          this.http.request(
            "get",
            environment.apiRoot + "/institution/opm-partners"
          ),
        ]).pipe(
          tap(([integrations, opmPartnersIntegrations]) => {
            if (integrations.errorMessage) {
              this.notifications.popNotification(
                "",
                "Error Retrieving Integrations at this time"
              );
            }
            integrations = integrations?.errorMessage ? [] : integrations;
            this._integrations.next({
              ...integrations,
              isLoading: false,
              isLoadingInitial: false,
            });
          })
        )
      : this.http.request("get", url, null, null, true).pipe(
          tap((integrations) => {
            if (integrations.errorMessage) {
              this.notifications.popNotification(
                "",
                "Error Retrieving Integrations at this time"
              );
            }
            integrations = integrations?.errorMessage ? [] : integrations;
            this._integrations.next({
              ...integrations,
              isLoading: false,
              isLoadingInitial: false,
            });
          })
        );
  }
  getRandomSecret() {
    const characters =
      "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    return Array.from(
      { length: 60 },
      () => characters[Math.floor(Math.random() * characters.length)]
    ).join("");
  }

  duplicateIntegration(integration) {
    const duplicateIntegration = cloneDeep(integration);
    duplicateIntegration.name = this.getNewIntegrationName();
    return this.createIntegrationNetworkCall(
      duplicateIntegration.name,
      {
        friendly_name: integration.json_data.friendly_name,
      },
      null,
      `copy_from=${integration.id}`
    )
      .pipe(
        catchError((error) => of({ error })),
        map(this.handleCreateResponse.bind(this))
      )
      .pipe(
        catchError((error) => of({ error })),
        tap((response: any) => {
          if (response.status === "ok") {
            const currentValue = this._integrations.getValue();
            const index = currentValue.items.findIndex(
              (_integration) => integration.name === _integration.name
            );
            if (index !== -1) {
              duplicateIntegration.id = response.id;
              currentValue.items.splice(index + 1, 0, duplicateIntegration);
            }
            this._integrations.next(currentValue);
          }
        })
      );
  }

  getNewIntegrationName() {
    const instanceDetails = this.environmentService.environment.domain.replace(
      /[^a-zA-Z0-9]/g,
      ""
    );
    const integrations = this._integrations.getValue();
    return `${instanceDetails.substring(0, 7)}_${integrations.totalItems + 1}`;
  }

  createIntegration(form, integrationType?, lmsSelected?) {
    const body = this.mapFormValuesFromForm({
      form,
      integrationType,
      lmsSelected,
      previousValues: false,
    });
    const jsonData = {
      secret: this.getRandomSecret(),
      ...body.json_data,
      // ...this.stripEmptyValues(body.json_data),
    };
    const integrationName = this.getNewIntegrationName();
    return this.createIntegrationNetworkCall(
      integrationName,
      jsonData
      // need to figure out if/how we incorporate partnerId
    )
      .pipe(
        catchError((error) => of({ error })),
        map(this.handleCreateResponse.bind(this))
      )
      .pipe(
        tap((response: any) => {
          this.startLoadingIntegrations = true;
          if (
            !response.error &&
            !response.errorMessage &&
            !response.errorType
          ) {
            if (!response.id && response?.id !== 0) {
              // no id returned so for now, reset to load without issues
              // return this.resetLoadingIntegrationsState();
            }

            this.updateInstitutionStepAsDone();

            const currentValue = this._integrations.value;
            this._integrations.next({
              isLoadingInitial: false,
              isLoading: false,
              items: [
                {
                  ...body,
                  created_at: new Date().toISOString(),
                  id: response.id,
                },
                ...currentValue.items,
              ],
              totalItems: currentValue.totalItems + 1,
            });
          }
        })
      );
  }

  updateInstitutionStepAsDone() {
    if (this?.environmentService?._details?.wizard_integration_set) return;
    this.http
      .request(
        "put",
        `${this.environmentService.environment.apiRoot}/institution/fields`,
        {
          wizard_integration_set: true,
        }
      )
      .subscribe();
  }

  stripEmptyValues(json_data) {
    const fieldsToAvoidDuplicating = [
      "status",
      "message",
      "tokenProcess",
      "termsProcess",
    ];
    return Object.keys(json_data).reduce((acc, property) => {
      const isAllAsterisks = /^\*+$/.test(json_data[property]);
      /^\*+$/.test(json_data[property]);
      return !fieldsToAvoidDuplicating.includes(property) ||
        ((json_data[property] || json_data[property] === false) &&
          !isAllAsterisks)
        ? { ...acc, [property]: json_data[property] }
        : acc;
    }, {}) as any;
  }

  handleUpdateResponse(response) {
    response = response?.[0]?.updated_at
      ? { status: "ok" }
      : { error: "Error Updating Integration", status: "error" };
    return response;
  }

  handleCreateResponse(response) {
    let id = response?.id || response.value?.rows[0]?.id;
    response =
      response?.rowCount === 1 || id || id === 0 || response.status === "ok"
        ? { status: "ok", id, name: response.name }
        : {
            error: "Error Creating Integration",
            status: "error",
          };
    return response;
  }

  createIntegrationNetworkCall(
    name,
    data,
    partnerId = null,
    additionalQueryParams?
  ) {
    let params = new URLSearchParams();
    if (additionalQueryParams) {
      additionalQueryParams.split("&").forEach((param) => {
        let [key, value] = param.split("=");
        params.append(key, value);
      });
    }
    if (partnerId) {
      params.append("partnerId", partnerId);
    }
    let integrationUrl = `${
      environment.apiRoot
    }/integrations/lms/${name}?${params.toString().trim()}`;

    return this.http
      .request("post", integrationUrl, { json_data: data }, null, true)
      .pipe(catchError((error) => of({ error })));
  }

  updateIntegration(form, integration, formSetup) {
    let shouldKickoffMassImport;
    if (this.termsService.hasChanges) {
      // if there are new terms, indicate mass import should occur on save
      shouldKickoffMassImport =
        !!this.termsService.getNewTermsToBeAdded().length;
    }
    const body = this.mapFormValuesFromForm({
      form,
      integrationType: integration?.integration_type,
      lmsSelected: integration?.json_data?.lms_type,
      previousValues: integration,
    });
    const updatedJsonData = this.stripValuesPriorToSave(
      body,
      integration,
      formSetup
    );
    return this.updateIntegrationNetworkCall(
      integration.integration_type,
      integration.name,
      updatedJsonData,
      integration.partnerId,
      shouldKickoffMassImport
    ).pipe(
      catchError((error) => of({ error })),
      map(this.handleUpdateResponse.bind(this)),
      tap((response: any) => {
        this.startLoadingIntegrations = true;
        if (!response.error && !response.errorMessage && !response.errorType) {
          // let currentValue = this._integrations.getValue();

          this.termsService.hasChanges = false;
          // currentValue.items.find((row, i) => {
          //   if (row.name === form.lmsConfigName) {

          //     currentValue.items[i] = { ...currentValue.items[i], ...body };
          //     currentValue.items[i].json_data.termsToPull = this.termsService.termChanges.current
          //     return true;
          //   }
          // });
          // this._integrations.next(currentValue);
        }
      })
    );
  }

  updateIntegrationNetworkCall(
    type,
    name,
    updatedData,
    partnerId = null,
    massImport = false,
    id = null,
    queryParams?: string
  ) {
    let integrationUrl = `${environment.apiRoot}/integrations/${type}/${name}?${queryParams}`;
    if (this.termsService.hasChanges) {
      updatedData.termsToPull = this.termsService._termChanges.current
        .map(({ id }) => id)
        .filter((term) => term);
    }
    if (id) {
      integrationUrl += `&id=${id}`;
    }

    if (partnerId != null) {
      integrationUrl += `&partnerId=${partnerId}`;
    }
    if (massImport) {
      integrationUrl += `&massImport=true`;
    }
    return this.http
      .request("put", integrationUrl, { json_data: updatedData }, null, true)
      .pipe(catchError((error) => of({ error })));
  }

  stripValuesPriorToSave(updatedData, originalData, formSetup) {
    const data = updatedData.json_data;
    const previousData = originalData.json_data;

    const jsonData = Object.keys(data).reduce((acc, field) => {
      const isAllAsterisks = /^\*+$/.test(data[field]);

      return isAllAsterisks || isEqual(data[field], previousData[field])
        ? acc
        : { ...acc, [field]: data[field] };
    }, {}) as any;

    return jsonData;
  }

  mapFormValuesFromForm({
    form,
    integrationType,
    lmsSelected,
    previousValues,
  }) {
    return form.sections.reduce(
      (result, section) => {
        // if question is from a flag and is already using defaultValue, avoid saving onto the integration
        section.questions
          .filter((question) => {
            if (!question.isFlag) return true;
            const property =
              question?.property && question?.property?.split("json_data.")?.[1]
                ? question?.property?.split("json_data.")?.[1]
                : null;
            if (!property) return true;
            const hasSetPreviousField =
              previousValues?.json_data &&
              previousValues.json_data.hasOwnProperty(property);

            return hasSetPreviousField
              ? !isEqual(question.control, previousValues?.json_data[property])
              : !isEqual(question.control, question.defaultValue);
          })
          .forEach((question) => {
            const property = question.property;
            let value = question.control;
            if (!Array.isArray(value)) {
              value =
                typeof value === "object" && value !== null
                  ? Object.keys(value).reduce((acc, prop) => {
                      acc[prop] =
                        acc[prop]?.trim !== undefined
                          ? acc[prop].trim()
                          : acc[prop];
                      return acc;
                    }, {})
                  : value?.trim !== undefined
                  ? value.trim()
                  : value;
            } else {
              value = value.map((element) => {
                if (
                  (typeof element === "object" &&
                    element !== null &&
                    element.value) ||
                  element.value === ""
                ) {
                  return element?.value?.trim() || "";
                }
                if (typeof element === "string") {
                  element =
                    element?.trim !== undefined ? element.trim() : element;
                }
                return element;
              });
            }
            const propertyParts = property?.split(".") || [];
            let currentObj = result;

            for (let i = 0; i < propertyParts.length - 1; i++) {
              const part = propertyParts[i];

              if (!currentObj[part]) {
                currentObj[part] = {};
              }

              currentObj = currentObj[part];
            }

            let lastPart = propertyParts[propertyParts.length - 1];
            currentObj[lastPart] = value;
          });

        return result;
      },
      {
        integration_type: integrationType,
        json_data: { lms_type: lmsSelected },
      }
    );
  }

  checkIfUniqueName(name) {
    return this.http
      .request("get", `${environment.apiRoot}/integrations`, null, null, true)
      .pipe(
        map(
          (response: any) =>
            !response?.find((integration) => integration?.name === name)
        )
      );
  }
}
